import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { slugify } from "../lib/utils";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { JobCard } from "../components/JobCard";
import { EmptyState } from "../components/EmptyState";
import { DistanceFilter, GpsNearButton } from "../components/DistanceFilter";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { QuickFilterChips } from "../components/QuickFilterChips";
import { SaveSearchButton } from "../components/SaveSearchButton";
import { ResultsMap } from "../components/ResultsMap";
import { ActiveFilterTags } from "../components/ActiveFilterTags";
import { Tag, Briefcase, ArrowRight, ChevronRight, MapPin, LayoutGrid, List, SlidersHorizontal } from "lucide-react";

const CATEGORY_ICONS = {
  "Restauração": "🍽️",
  "Logística": "🚚",
  "Retalho": "🛒",
  "Hotelaria": "🏨",
  "Limpeza": "🧹",
  "Apoio ao Cliente": "🎧",
  "Construção": "🏗️",
  "Saúde": "🏥",
  "Outros": "💼",
};

const TOP_CITIES = ["Lisboa", "Porto", "Braga", "Amadora", "Almada", "Setúbal", "Matosinhos", "Guimarães"];
const ALL = "__all__";

function toCountMap(facetArr) {
  const m = {};
  (facetArr || []).forEach((f) => { m[f.value] = f.count; });
  return m;
}

function FilterSelect({ label, value, onChange, options, testId, counts }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">{label}</p>
      <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
        <SelectTrigger data-testid={testId} className="bg-white"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={ALL}>Todos</SelectItem>
          {options.map((o) => {
            const c = counts ? counts[o] : undefined;
            return <SelectItem key={o} value={o}>{o}{c != null ? ` (${c})` : ""}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function JobsByCategory() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const PAGE_SIZE = 24;
  const [category, setCategory] = useState(null);
  const [meta, setMeta] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [facets, setFacets] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const [otherCategories, setOtherCategories] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("f2j_jobs_view") || "grid"; } catch { return "grid"; }
  });
  const get = (k) => params.get(k) || "";

  useEffect(() => {
    try { localStorage.setItem("f2j_jobs_view", view); } catch { /* ignore */ }
  }, [view]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setJobs(null);
    setNotFound(false);
    setCategory(null);
    api.get("/meta").then((r) => {
      const m = r.data;
      setMeta(m);
      const match = m.categories.find((c) => slugify(c) === slug);
      if (match) {
        setCategory(match);
        setOtherCategories(m.categories.filter((c) => c !== match));
      } else {
        setNotFound(true);
        setJobs([]);
      }
    });
  }, [slug]);

  const buildParams = useCallback(() => {
    if (!category) return null;
    const p = { category };
    ["q", "district", "concelho", "regime", "work_mode", "days", "featured", "has_cover",
     "near_district", "near_concelho", "radius_km", "sort"].forEach((k) => {
      if (params.get(k)) p[k] = params.get(k);
    });
    return p;
  }, [params, category]);

  // Cada alteração de filtros dispara um novo pedido; respostas antigas são ignoradas
  // (evita que um pedido lento de um filtro anterior substitua os resultados atuais).
  const fetchJobs = useCallback(() => {
    const base = buildParams();
    if (!base) return;
    const id = ++reqId.current;
    setLoading(true);
    api.get("/jobs", { params: { ...base, limit: PAGE_SIZE, offset: 0 } }).then((r) => {
      if (id !== reqId.current) return;
      setJobs(r.data.jobs || []);
      setTotal(r.data.total || 0);
      setHasMore(!!r.data.has_more);
      setFacets(r.data.facets || {});
    }).catch(() => {
      if (id !== reqId.current) return;
      setJobs([]); setTotal(0); setHasMore(false); setFacets({});
    }).finally(() => { if (id === reqId.current) setLoading(false); });
  }, [buildParams]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const loadMore = async () => {
    const base = buildParams();
    if (loadingMore || !jobs || !base) return;
    const id = reqId.current;
    setLoadingMore(true);
    try {
      const r = await api.get("/jobs", { params: { ...base, limit: PAGE_SIZE, offset: jobs.length } });
      if (id !== reqId.current) return;
      setJobs((prev) => [...(prev || []), ...(r.data.jobs || [])]);
      setHasMore(!!r.data.has_more);
      setTotal(r.data.total || 0);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k === "district") {
      next.delete("concelho");
      ["near_district", "near_concelho", "radius_km"].forEach((x) => next.delete(x));
    }
    setParams(next);
  };

  const setFilters = (obj) => {
    const next = new URLSearchParams(params);
    Object.entries(obj).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    setParams(next);
  };

  const concelhos = meta && get("district") ? meta.districts[get("district")] || [] : [];

  const seoData = useMemo(() => ({
    title: category
      ? `Empregos de ${category} — Vagas em Portugal | FIRST2JOB`
      : "Empregos por categoria | FIRST2JOB",
    description: category
      ? `Encontra ofertas de emprego em ${category} em Portugal. ${total} vaga${total !== 1 ? "s" : ""} disponível${total !== 1 ? "s" : ""}. Candidata-te gratuitamente no FIRST2JOB.`
      : "",
  }), [category, total]);
  useSEO(seoData);

  const icon = category ? (CATEGORY_ICONS[category] || "💼") : "💼";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      {/* Hero */}
      <div className="bg-slate-950 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <nav className="flex items-center gap-1.5 text-slate-500 text-sm mb-4">
            <Link to="/" className="hover:text-slate-300 transition-colors">Início</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/empregos" className="hover:text-slate-300 transition-colors">Empregos</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-slate-300">{category || slug}</span>
          </nav>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center shrink-0 text-2xl">
              {icon}
            </div>
            <div>
              <h1 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-white">
                Empregos de {category || slug}
              </h1>
              <p className="text-slate-400 text-sm mt-1.5">
                {jobs == null
                  ? "A carregar..."
                  : `${total} vaga${total !== 1 ? "s" : ""} ${get("near_concelho") ? `até ${get("radius_km") || 25} km de ${get("near_concelho")}` : get("concelho") || get("district") ? `em ${get("concelho") || get("district")}` : "em Portugal"}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {notFound ? (
          <EmptyState icon={Tag} title="Categoria não encontrada"
            description="Esta categoria não existe. Verifica o endereço ou explora todas as vagas."
            actionLabel="Ver todas as vagas" onAction={() => (window.location.href = "/empregos")} />
        ) : (
          <>
            <div className="lg:hidden mb-4 flex gap-2">
              <GpsNearButton setFilters={setFilters} radius={get("radius_km")} className="shrink-0" />
              <Button data-testid="toggle-filters-btn" variant="outline" className="rounded-full h-11 flex-1" onClick={() => setShowFilters(!showFilters)}>
                <SlidersHorizontal className="w-4 h-4 mr-2" /> {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <aside className={`${showFilters ? "block" : "hidden"} lg:block space-y-5`}>
                {meta && (
                  <>
                    <DistanceFilter meta={meta} get={get} setFilters={setFilters} districtCounts={toCountMap(facets.district)} />
                    <FilterSelect label="Distrito" testId="filter-district" value={get("district")} onChange={(v) => setFilter("district", v)} options={Object.keys(meta.districts)} counts={toCountMap(facets.district)} />
                    {concelhos.length > 0 && (
                      <FilterSelect label="Concelho" testId="filter-concelho" value={get("concelho")} onChange={(v) => setFilter("concelho", v)} options={concelhos} counts={toCountMap(facets.concelho)} />
                    )}
                    <FilterSelect label="Regime" testId="filter-regime" value={get("regime")} onChange={(v) => setFilter("regime", v)} options={meta.regimes} counts={toCountMap(facets.regime)} />
                    <FilterSelect label="Modalidade" testId="filter-workmode" value={get("work_mode")} onChange={(v) => setFilter("work_mode", v)} options={meta.work_modes} counts={toCountMap(facets.work_mode)} />
                    <FilterSelect label="Publicado" testId="filter-days" value={get("days")} onChange={(v) => setFilter("days", v)} options={["1", "3", "7", "30"]} />
                  </>
                )}
              </aside>

              <main data-testid="jobs-results" data-loading={loading ? "true" : "false"}
                className={`lg:col-span-3 transition-opacity ${loading && jobs != null ? "opacity-50" : ""}`}>
                {jobs == null ? (
                  <div className="text-center text-slate-400 py-20">A carregar vagas...</div>
                ) : (
                  <div className="space-y-4">
                    <ResultsMap filters={buildParams()}
                      onSelectConcelho={get("near_concelho") ? undefined : (pt) => setFilters({ district: pt.district, concelho: pt.concelho, near_district: "" })} />
                    <QuickFilterChips params={params} onToggle={setFilter}
                      onClear={(keys) => setFilters(Object.fromEntries(keys.map((k) => [k, ""])))}
                      facets={facets} />
                    <ActiveFilterTags params={params} hide={["category"]}
                      onRemove={(keys) => setFilters(Object.fromEntries(keys.map((k) => [k, ""])))}
                      onClearAll={() => setParams({})} />
                    {jobs.length === 0 ? (
                  <EmptyState icon={Briefcase} title={`Sem vagas de ${category}`}
                    description="Ainda não há vagas com estes filtros. Experimenta remover alguns filtros."
                    actionLabel="Limpar filtros" onAction={() => setParams({})} />
                    ) : (
                    <>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p data-testid="jobs-count" className="text-sm text-slate-500">{total} vaga{total !== 1 ? "s" : ""} encontrada{total !== 1 ? "s" : ""}{jobs.length < total ? ` · a mostrar ${jobs.length}` : ""}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <SaveSearchButton extraFilters={category ? { category } : null} />
                        <Select value={get("sort") || "recent"} onValueChange={(v) => setFilter("sort", v === "recent" ? "" : v)}>
                          <SelectTrigger data-testid="sort-select" className="bg-white h-9 rounded-full text-xs font-semibold w-[180px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recent">Mais recentes</SelectItem>
                            <SelectItem value="salary">Melhor pagas</SelectItem>
                            <SelectItem value="distance" disabled={!get("near_concelho")}>
                              {get("near_concelho") ? "Mais perto de mim" : "Mais perto (define concelho)"}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" role="group" aria-label="Modo de visualização">
                          <button
                            type="button"
                            data-testid="view-list-btn"
                            aria-pressed={view === "list"}
                            onClick={() => setView("list")}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                          >
                            <List className="w-3.5 h-3.5" /> Lista
                          </button>
                          <button
                            type="button"
                            data-testid="view-grid-btn"
                            aria-pressed={view === "grid"}
                            onClick={() => setView("grid")}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                          >
                            <LayoutGrid className="w-3.5 h-3.5" /> Grid
                          </button>
                        </div>
                      </div>
                    </div>
                    <div
                      data-testid="jobs-container"
                      data-view={view}
                      className={view === "grid" ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" : "space-y-4"}
                    >
                      {jobs.map((j) => <JobCard key={j.id} job={j} compact={view === "grid"} />)}
                    </div>
                    {hasMore && (
                      <div className="flex justify-center pt-4">
                        <Button data-testid="load-more-btn" variant="outline" className="rounded-full h-11 px-6" disabled={loadingMore} onClick={loadMore}>
                          {loadingMore ? "A carregar..." : `Ver mais vagas (${total - jobs.length} restantes)`}
                        </Button>
                      </div>
                    )}
                    </>
                    )}
                  </div>
                )}
              </main>
            </div>
          </>
        )}

        {/* Top cities for this category */}
        {category && (
          <div className="mt-12 pt-8 border-t border-black/5">
            <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-4">
              {category} por cidade
            </h2>
            <div className="flex flex-wrap gap-2">
              {TOP_CITIES.map((city) => (
                <Link key={city}
                  to={`/empregos?category=${encodeURIComponent(category)}&concelho=${encodeURIComponent(city)}`}
                  className="flex items-center gap-1.5 bg-white border border-black/5 rounded-full px-4 py-1.5 text-sm text-slate-700 hover:border-amber-400 hover:text-amber-600 transition-colors">
                  <MapPin className="w-3 h-3" /> {city} <ArrowRight className="w-3 h-3 ml-0.5" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Other categories */}
        {otherCategories.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading font-bold tracking-tighter text-lg text-slate-900 mb-4">Outras categorias</h2>
            <div className="flex flex-wrap gap-2">
              {otherCategories.map((cat) => (
                <Link key={cat} to={`/empregos-de/${slugify(cat)}`}
                  className="bg-white border border-black/5 rounded-full px-4 py-1.5 text-sm text-slate-700 hover:border-amber-400 hover:text-amber-600 transition-colors">
                  {CATEGORY_ICONS[cat] || "💼"} {cat}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-slate-950 rounded-2xl p-8 text-center">
          <p className="font-heading font-black tracking-tighter text-2xl text-white mb-2">Alerta de {category || "empregos"}</p>
          <p className="text-slate-400 text-sm mb-5">Recebe novas vagas de {category || "emprego"} diretamente no teu email assim que forem publicadas.</p>
          <Link to="/auth"
            className="inline-block bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold rounded-full px-6 py-2.5 text-sm transition-colors">
            Criar alerta gratuito
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
